/* v3.18-1302 — EMAIL-ARCH-003
 *
 * Email Template Library — Admin Page
 *
 * Restricted to Prestigious Companies, Prestigious Travel Club, and
 * EasyQuickFund.  Access is enforced backend-side (HTTP 403) and
 * frontend-side (menu hidden + route probe).  Users without write
 * scope see the library in read-only mode.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Search, Plus, Edit2, Archive, RotateCcw, Trash2, ChevronDown, ChevronRight,
  Loader2, ShieldAlert, Palette, FileText, History, X,
} from 'lucide-react';

const emptyTemplate = {
  name: '',
  description: '',
  category_key: '',
  tags: [],
  subject: '',
  body_text: '',
  body_html: '',
};

const emptyCategory = {
  name: '',
  description: '',
  icon: 'Mail',
  color: '#64748b',
};

export default function EmailTemplateLibraryPage() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null); // null=loading, {allowed_read, allowed_write}

  const [categories, setCategories] = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [query, setQuery]           = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [openCats, setOpenCats]     = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFull, setSelectedFull] = useState(null); // full row w/ body_html
  const [previewRendered, setPreviewRendered] = useState(null);  // v3.18-1303 sample-data preview
  const [previewLoading,  setPreviewLoading]  = useState(false);
  const [versions, setVersions]     = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  // Editors
  const [tplEditorOpen, setTplEditorOpen] = useState(false);
  const [tplDraft, setTplDraft] = useState(emptyTemplate);
  const [savingTpl, setSavingTpl] = useState(false);

  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [catDraftOpen, setCatDraftOpen] = useState(false);
  const [catDraft, setCatDraft] = useState(emptyCategory);
  const [catDraftId, setCatDraftId] = useState(null);
  const [savingCat, setSavingCat] = useState(false);

  const readOnly = access && !access.allowed_write;

  // ── Initial load ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/email-template-library/access');
        setAccess(data);
        if (!data.allowed_read) return;
        await refreshAll();
      } catch (e) {
        if (e?.response?.status === 403) {
          setAccess({ allowed_read: false, allowed_write: false });
        }
      }
    })();
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, m] = await Promise.all([
        api.get(`/email-template-library/categories?include_archived=${showArchived}`),
        api.get(`/email-template-library/templates?include_archived=${showArchived}&limit=1000`),
        api.get('/email-template-library/merge-fields'),
      ]);
      setCategories(c.data?.items || []);
      setTemplates(t.data?.items || []);
      setMergeFields(m.data?.items || []);
      // Expand top 3 categories by default so the library feels alive
      const initial = {};
      (c.data?.items || []).slice(0, 3).forEach((cat) => { initial[cat.key] = true; });
      setOpenCats((prev) => Object.keys(prev).length ? prev : initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { if (access?.allowed_read) refreshAll(); }, [showArchived, refreshAll, access]);

  const loadFull = useCallback(async (tplId) => {
    setSelectedId(tplId);
    setSelectedFull(null);
    setVersions([]);
    setPreviewRendered(null);
    if (!tplId) return;
    try {
      const [f, v] = await Promise.all([
        api.get(`/email-template-library/templates/${tplId}`),
        api.get(`/email-template-library/templates/${tplId}/versions`),
      ]);
      setSelectedFull(f.data?.item || null);
      setVersions(v.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load template');
    }
  }, []);

  // v3.18-1303 — sample-data preview.  Admins can render any template
  // with realistic merge-field values before publishing.
  const runSamplePreview = useCallback(async () => {
    if (!selectedFull) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/email-template-library/templates/${selectedFull.id}/render`, {
        context: {
          client_first_name: 'Sarah',
          client_last_name:  'Johnson',
          client_full_name:  'Sarah Johnson',
          client_email:      'sarah.johnson@example.com',
          client_phone:      '+1 (555) 123-4567',
          company_name:      'Prestigious Travel Club',
          deal_number:       'PTC-2026-0142',
          deal_amount:       12500.0,
          approved_amount:   12500.0,
          funded_amount:     12500.0,
          payment_amount:    500.0,
          payment_date:      'Feb 20, 2026',
          invoice_number:    'INV-2026-0089',
          invoice_amount:    500.0,
          invoice_due_date:  'Mar 1, 2026',
          outstanding_amount: 250.0,
          auth_amount:       500.0,
          auth_confirmation: 'AUTH-88421',
          ach_amount:        500.0,
          ach_date:          'Feb 20, 2026',
          appt_date:         'Feb 22, 2026',
          appt_time:         '10:30 AM ET',
          renewal_date:      'Mar 15, 2026',
          statement_period:  'January 2026',
          delay_reason:      'Bank verification pending',
          status_note:       'Underwriting review scheduled for next week',
          docs_needed_bullets: " \u2022 Driver's license\n \u2022 Proof of income\n \u2022 Bank statement (last 2 months)",
          call_recap_notes:  " - Discussed timeline\n - Reviewed documents\n - Agreed to reconvene Friday",
          announcement_subject: 'New service now available',
          announcement_body:  'We are excited to launch a new concierge tier just for you.',
          compliance_notice_body: 'This is a legally required disclosure notice.',
          handoff_to_name:   'Taylor Kim',
          handoff_context:   'Client wants a follow-up on funding.',
          partner_first_name: 'Sam',
          payout_amount:     2500.0,
          recipient_first_name: 'Jordan',
          reset_at:          'Feb 20, 2026 at 10:12 AM',
          activity_summary:  'A new login was recorded from IP 1.2.3.4',
        },
      });
      setPreviewRendered(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to render sample preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFull]);

  // ── Filtering ─────────────────────────────────────────────────────
  const allTags = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) => {
      if (selectedTag && !(t.tags || []).includes(selectedTag)) return false;
      if (!q) return true;
      return (
        (t.name || '').toLowerCase().includes(q) ||
        (t.subject || '').toLowerCase().includes(q) ||
        (t.body_text || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.key || '').toLowerCase().includes(q)
      );
    });
  }, [templates, query, selectedTag]);

  const grouped = useMemo(() => {
    const m = new Map();
    categories.forEach((cat) => m.set(cat.key, { cat, items: [] }));
    filtered.forEach((t) => {
      const bucket = m.get(t.category_key);
      if (bucket) bucket.items.push(t);
    });
    return Array.from(m.values());
  }, [categories, filtered]);

  const toggleCat = (key) => setOpenCats((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Template editor ───────────────────────────────────────────────
  const openNewTemplate = () => {
    const firstCat = categories[0]?.key || '';
    setTplDraft({ ...emptyTemplate, category_key: firstCat });
    setTplEditorOpen(true);
  };

  const openEditTemplate = () => {
    if (!selectedFull) return;
    setTplDraft({
      id:           selectedFull.id,
      name:         selectedFull.name,
      description:  selectedFull.description || '',
      category_key: selectedFull.category_key,
      tags:         selectedFull.tags || [],
      subject:      selectedFull.subject || '',
      body_text:    selectedFull.body_text || '',
      body_html:    selectedFull.body_html || '',
    });
    setTplEditorOpen(true);
  };

  const insertMerge = (field) => {
    const marker = `{{${field}}}`;
    // Append to body_text by default — operators can position from there
    setTplDraft((d) => ({ ...d, body_text: `${d.body_text || ''}${marker}` }));
  };

  const saveTemplate = async () => {
    if (!tplDraft.name?.trim() || !tplDraft.subject?.trim() || !tplDraft.body_text?.trim()) {
      toast.error('Name, subject, and body are required');
      return;
    }
    setSavingTpl(true);
    try {
      let saved;
      if (tplDraft.id) {
        const { data } = await api.patch(`/email-template-library/templates/${tplDraft.id}`, {
          name:         tplDraft.name,
          description:  tplDraft.description,
          category_key: tplDraft.category_key,
          tags:         tplDraft.tags,
          subject:      tplDraft.subject,
          body_text:    tplDraft.body_text,
          body_html:    tplDraft.body_html || `<p>${(tplDraft.body_text || '').replace(/\n/g, '<br>')}</p>`,
        });
        saved = data.item;
        toast.success('Template updated');
      } else {
        const { data } = await api.post('/email-template-library/templates', {
          name:         tplDraft.name,
          description:  tplDraft.description,
          category_key: tplDraft.category_key,
          tags:         tplDraft.tags,
          subject:      tplDraft.subject,
          body_text:    tplDraft.body_text,
          body_html:    tplDraft.body_html || `<p>${(tplDraft.body_text || '').replace(/\n/g, '<br>')}</p>`,
        });
        saved = data.item;
        toast.success('Template created');
      }
      setTplEditorOpen(false);
      await refreshAll();
      await loadFull(saved.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTpl(false);
    }
  };

  const archiveTemplate = async () => {
    if (!selectedFull) return;
    if (!window.confirm('Archive this template? It will remain in history but stop appearing in the picker.')) return;
    try {
      await api.post(`/email-template-library/templates/${selectedFull.id}/archive`);
      toast.success('Template archived');
      await refreshAll();
      await loadFull(selectedFull.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const restoreTemplate = async () => {
    if (!selectedFull) return;
    try {
      await api.post(`/email-template-library/templates/${selectedFull.id}/restore`);
      toast.success('Template restored');
      await refreshAll();
      await loadFull(selectedFull.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore');
    }
  };

  const deleteTemplate = async () => {
    if (!selectedFull) return;
    if (!window.confirm('Permanently delete this template?  This action is audited.')) return;
    try {
      await api.delete(`/email-template-library/templates/${selectedFull.id}`);
      toast.success('Template deleted');
      setSelectedId(null);
      setSelectedFull(null);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── Category management ───────────────────────────────────────────
  const openNewCategory = () => {
    setCatDraft(emptyCategory);
    setCatDraftId(null);
    setCatDraftOpen(true);
  };
  const openEditCategory = (cat) => {
    setCatDraft({ name: cat.name, description: cat.description || '', icon: cat.icon || 'Mail', color: cat.color || '#64748b' });
    setCatDraftId(cat.id);
    setCatDraftOpen(true);
  };
  const saveCategory = async () => {
    if (!catDraft.name?.trim()) { toast.error('Name is required'); return; }
    setSavingCat(true);
    try {
      if (catDraftId) {
        await api.patch(`/email-template-library/categories/${catDraftId}`, catDraft);
        toast.success('Category updated');
      } else {
        await api.post('/email-template-library/categories', catDraft);
        toast.success('Category created');
      }
      setCatDraftOpen(false);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save category');
    } finally {
      setSavingCat(false);
    }
  };
  const archiveCategory = async (cat) => {
    try {
      await api.patch(`/email-template-library/categories/${cat.id}`, { is_archived: !cat.is_archived });
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update category');
    }
  };
  const reorderCategory = async (cat, delta) => {
    try {
      await api.patch(`/email-template-library/categories/${cat.id}`, { sort_order: (cat.sort_order || 0) + delta });
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reorder');
    }
  };
  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?  Only unused categories can be deleted.`)) return;
    try {
      await api.delete(`/email-template-library/categories/${cat.id}`);
      toast.success('Category deleted');
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot delete — likely still in use.');
    }
  };

  // ── Guards ────────────────────────────────────────────────────────
  if (access === null) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 size={14} className="animate-spin" /> Checking access…
      </div>
    );
  }
  if (!access.allowed_read) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center" data-testid="template-library-forbidden">
        <ShieldAlert size={40} className="mx-auto text-red-600 mb-2" />
        <h2 className="text-lg font-semibold mb-1">Access restricted</h2>
        <p className="text-sm text-slate-600 mb-4">
          The Email Template Library is restricted to Prestigious Companies,
          Prestigious Travel Club, and EasyQuickFund users.
        </p>
        <Button variant="outline" onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-[1600px] mx-auto" data-testid="email-template-library-page">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Email Template Library</h1>
          <p className="text-sm text-slate-500">
            Corporate communications templates for Prestigious Companies, Prestigious Travel Club, and EasyQuickFund.
          </p>
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <>
              <Button variant="outline" onClick={() => setCatManagerOpen(true)} data-testid="template-library-manage-cats">
                <Palette size={14} className="mr-1" /> Manage Categories
              </Button>
              <Button onClick={openNewTemplate} data-testid="template-library-new-tpl">
                <Plus size={14} className="mr-1" /> New Template
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — search + tags + tree */}
        <div className="col-span-4 border rounded flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <div className="p-2 border-b bg-slate-50 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="pl-7 h-8 text-sm"
                data-testid="template-library-search"
              />
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              {allTags.slice(0, 10).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${selectedTag === tag ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 hover:bg-slate-100'}`}
                >
                  {tag}
                </button>
              ))}
              <label className="text-[10px] flex items-center gap-1 ml-auto">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="template-library-show-archived" />
                <span>Archived</span>
              </label>
            </div>
          </div>
          <ScrollArea className="flex-1">
            {loading && (
              <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            )}
            {!loading && grouped.map(({ cat, items }) => (
              <div key={cat.key} className="border-b">
                <button
                  type="button"
                  onClick={() => toggleCat(cat.key)}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 text-left"
                  data-testid={`template-library-cat-${cat.key}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-1.5 h-4 rounded-sm" style={{ background: cat.color || '#64748b' }} />
                    <span className="text-sm font-medium truncate">{cat.name}</span>
                    <span className="text-[10px] text-slate-500">({items.length})</span>
                    {cat.is_archived && <Badge variant="outline" className="text-[9px]">archived</Badge>}
                  </div>
                  {(openCats[cat.key] || query) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {(openCats[cat.key] || query) && (
                  <div className="pl-4 pb-1">
                    {items.length === 0 ? (
                      <div className="text-xs text-slate-400 italic px-2 py-1">No templates.</div>
                    ) : (
                      items.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => loadFull(tpl.id)}
                          className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-slate-100 flex items-center gap-1 ${selectedId === tpl.id ? 'bg-slate-100 font-medium' : ''}`}
                          data-testid={`template-library-item-${tpl.key}`}
                        >
                          <FileText size={11} className="text-slate-400 shrink-0" />
                          <span className="truncate">{tpl.name}</span>
                          {tpl.is_archived && <Badge variant="outline" className="text-[9px] ml-auto">archived</Badge>}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
            {!loading && grouped.length === 0 && (
              <div className="p-4 text-sm text-slate-500 text-center">No templates.</div>
            )}
          </ScrollArea>
        </div>

        {/* RIGHT — details */}
        <div className="col-span-8 border rounded flex flex-col overflow-hidden" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {!selectedFull ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-4">
              Select a template to view details.
            </div>
          ) : (
            <>
              <div className="p-3 border-b bg-slate-50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-base" data-testid="template-detail-name">{selectedFull.name}</span>
                      {selectedFull.is_archived && <Badge variant="outline">archived</Badge>}
                      {selectedFull.is_active === false && <Badge variant="secondary">inactive</Badge>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {selectedFull.key} · v{selectedFull.version || 1} · {selectedFull.category_key}
                    </div>
                    {selectedFull.description && (
                      <div className="text-xs text-slate-600 mt-1">{selectedFull.description}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(selectedFull.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" onClick={openEditTemplate} data-testid="template-detail-edit"><Edit2 size={12} className="mr-1" />Edit</Button>
                      {selectedFull.is_archived
                        ? <Button size="sm" variant="outline" onClick={restoreTemplate} data-testid="template-detail-restore"><RotateCcw size={12} className="mr-1" />Restore</Button>
                        : <Button size="sm" variant="outline" onClick={archiveTemplate} data-testid="template-detail-archive"><Archive size={12} className="mr-1" />Archive</Button>
                      }
                      <Button size="sm" variant="destructive" onClick={deleteTemplate} data-testid="template-detail-delete"><Trash2 size={12} className="mr-1" />Delete</Button>
                    </div>
                  )}
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mb-1">Subject</div>
                    <div className="text-sm border rounded px-2 py-1 bg-white">{selectedFull.subject}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mb-1">Plain-text body</div>
                    <div className="border rounded p-3 bg-white text-sm whitespace-pre-wrap">{selectedFull.body_text}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mb-1">HTML preview</div>
                    <div className="border rounded p-3 bg-white text-sm" dangerouslySetInnerHTML={{ __html: selectedFull.body_html || '' }} />
                  </div>
                  {selectedFull.merge_fields?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 mb-1">Merge fields used</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedFull.merge_fields.map((f) => (
                          <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* v3.18-1303 — Preview with sample data */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase text-slate-500">Preview with sample data</div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={runSamplePreview}
                        disabled={previewLoading}
                        data-testid="template-detail-sample-preview"
                      >
                        {previewLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                        {previewRendered ? 'Refresh Preview' : 'Preview with Sample Data'}
                      </Button>
                    </div>
                    {previewRendered && (
                      <div className="space-y-2 border rounded p-3 bg-slate-50">
                        <div>
                          <div className="text-[10px] uppercase text-slate-500 mb-0.5">Subject (rendered)</div>
                          <div className="text-sm border rounded px-2 py-1 bg-white" data-testid="template-sample-subject">
                            {previewRendered.rendered.subject}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-500 mb-0.5">Body (rendered)</div>
                          <div className="border rounded p-3 bg-white text-sm whitespace-pre-wrap" data-testid="template-sample-body">
                            {previewRendered.rendered.body_text}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-500 mb-0.5">HTML (rendered)</div>
                          <div className="border rounded p-3 bg-white text-sm" dangerouslySetInnerHTML={{ __html: previewRendered.rendered.body_html || '' }} />
                        </div>
                      </div>
                    )}
                  </div>
                  {versions.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 mb-1 flex items-center gap-1"><History size={11} /> Version history</div>
                      <div className="text-xs space-y-1">
                        {versions.map((v) => (
                          <div key={v.id} className="border rounded px-2 py-1 bg-white">
                            <div><span className="font-medium">v{v.version}</span> · {new Date(v.snapshot_at).toLocaleString()} · by {v.snapshot_by?.name || v.snapshot_by?.email || 'System'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </div>
      </div>

      {/* ── Template Editor ──────────────────────────────────────── */}
      <Dialog open={tplEditorOpen} onOpenChange={setTplEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle>{tplDraft.id ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription>Templates render via merge fields at send-time.  Dynamic signature is appended automatically by Compose &amp; Approve.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1">
            <div className="grid grid-cols-3 gap-3 p-1">
              <div className="col-span-2 space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input value={tplDraft.name} onChange={(e) => setTplDraft({ ...tplDraft, name: e.target.value })} data-testid="template-editor-name" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={tplDraft.description} onChange={(e) => setTplDraft({ ...tplDraft, description: e.target.value })} data-testid="template-editor-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={tplDraft.category_key} onValueChange={(v) => setTplDraft({ ...tplDraft, category_key: v })}>
                      <SelectTrigger data-testid="template-editor-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                      <SelectContent>
                        {categories.filter((c) => !c.is_archived).map((c) => (
                          <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={(tplDraft.tags || []).join(', ')}
                      onChange={(e) => setTplDraft({ ...tplDraft, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                      data-testid="template-editor-tags"
                    />
                  </div>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={tplDraft.subject} onChange={(e) => setTplDraft({ ...tplDraft, subject: e.target.value })} data-testid="template-editor-subject" />
                </div>
                <div>
                  <Label>Plain-text body</Label>
                  <Textarea rows={10} value={tplDraft.body_text} onChange={(e) => setTplDraft({ ...tplDraft, body_text: e.target.value })} data-testid="template-editor-body-text" />
                </div>
                <div>
                  <Label>HTML body (optional — auto-generated from plain-text when empty)</Label>
                  <Textarea rows={6} value={tplDraft.body_html} onChange={(e) => setTplDraft({ ...tplDraft, body_html: e.target.value })} data-testid="template-editor-body-html" />
                </div>
              </div>
              <div className="col-span-1">
                <Label>Merge fields</Label>
                <div className="text-[11px] text-slate-500 mb-2">Click to append at cursor position of the plain body.</div>
                <div className="border rounded p-2 max-h-[500px] overflow-y-auto space-y-3 bg-slate-50" data-testid="template-editor-merge-fields">
                  {Array.from(new Set(mergeFields.map((f) => f.group))).map((group) => (
                    <div key={group}>
                      <div className="text-[10px] uppercase text-slate-500 mb-1">{group}</div>
                      <div className="flex flex-wrap gap-1">
                        {mergeFields.filter((f) => f.group === group).map((f) => (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => insertMerge(f.key)}
                            className="text-[10px] bg-white border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-900 hover:text-white transition-colors"
                            title={f.label}
                          >
                            {`{{${f.key}}}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={savingTpl} data-testid="template-editor-save">
              {savingTpl ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              {tplDraft.id ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Category Manager ─────────────────────────────────────── */}
      <Dialog open={catManagerOpen} onOpenChange={setCatManagerOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="category-manager-dialog">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Create, rename, reorder, or archive corporate communication categories.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mb-2">
            <Button size="sm" onClick={openNewCategory} data-testid="category-manager-new">
              <Plus size={12} className="mr-1" /> New Category
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="space-y-1">
              {categories.map((cat) => (
                <div key={cat.id} className="border rounded flex items-center gap-2 px-2 py-1" data-testid={`category-manager-row-${cat.key}`}>
                  <span className="w-1.5 h-6 rounded-sm shrink-0" style={{ background: cat.color || '#64748b' }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{cat.name}</div>
                    <div className="text-[10px] text-slate-500">{cat.key} · {cat.template_total ?? 0} template(s)</div>
                  </div>
                  {cat.is_archived && <Badge variant="outline" className="text-[9px]">archived</Badge>}
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => reorderCategory(cat, -1)} title="Move up"><ChevronDown size={12} className="rotate-180" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => reorderCategory(cat, 1)}  title="Move down"><ChevronDown size={12} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEditCategory(cat)} title="Edit"><Edit2 size={12} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => archiveCategory(cat)} title={cat.is_archived ? 'Restore' : 'Archive'}>
                      {cat.is_archived ? <RotateCcw size={12} /> : <Archive size={12} />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteCategory(cat)} title="Delete"><Trash2 size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatManagerOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Category Draft ───────────────────────────────────────── */}
      <Dialog open={catDraftOpen} onOpenChange={setCatDraftOpen}>
        <DialogContent className="max-w-md" data-testid="category-draft-dialog">
          <DialogHeader>
            <DialogTitle>{catDraftId ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={catDraft.name} onChange={(e) => setCatDraft({ ...catDraft, name: e.target.value })} data-testid="category-draft-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={catDraft.description} onChange={(e) => setCatDraft({ ...catDraft, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Icon</Label>
                <Input value={catDraft.icon} onChange={(e) => setCatDraft({ ...catDraft, icon: e.target.value })} placeholder="Mail" />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-1 items-center">
                  <input type="color" value={catDraft.color} onChange={(e) => setCatDraft({ ...catDraft, color: e.target.value })} className="h-8 w-10 border rounded" />
                  <Input value={catDraft.color} onChange={(e) => setCatDraft({ ...catDraft, color: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDraftOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory} disabled={savingCat} data-testid="category-draft-save">
              {savingCat ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              {catDraftId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
